import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { nameDataset } from '@/entities/dataset';
import {
  ACTIVATE_RULE_PERMISSION,
  READ_CLASSIFICATIONS_PERMISSION,
  READ_INCIDENTS_PERMISSION,
  READ_PLANS_PERMISSION,
  READ_RULES_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import { useDatasetNames } from '@/features/catalog-browser';
import { useReviewQueue } from '@/features/classification-review';
import { subjectIsDataset, useIncidentQueue } from '@/features/incidents';
import { relationOf, usePlanQueue } from '@/features/remediation';
import { describeAge } from '@/features/home';

import { SCREEN_PATHS } from '@/shared/routes';

import { useApprovalFeed } from '../api/approvals';
import {
  SOURCE_LABEL_KEY,
  SOURCE_STATE_KEY,
  approvalNotification,
  badgeFor,
  blindSpots,
  incidentNotification,
  remediationApprovalNotification,
  minutesSince,
  orderNotifications,
  pruneDismissed,
  readDismissed,
  reviewNotification,
  sourceStateFor,
  unreadCount,
  writeDismissed,
} from '../model/notifications';
import type { Access, Notification, SourceReport } from '../model/notifications';

/* The notification centre: what is waiting for this person, in the app chrome.
 *
 * **A rule waiting for a second signature is the case this exists for.** `kernel.approval` refuses an
 * activation until two people other than the requester have signed, and until now the only way to
 * learn that a request was waiting was to open that rule's own page — so an approver had no queue to
 * work and an author no way to see the first signature arrive. The platform has told people to
 * collect two signatures since `core.rules` was built; this is where they find out they were asked.
 *
 * **Derived from three reads that already exist. There is no notification API and none is invented.**
 * Draft rules' open transition requests (bounded — see `useApprovalFeed`), the incident queue, and
 * the PII review queue. Every hook comes through its feature's `index.ts`, and nothing here refetches
 * something another screen already fetches: the approval lookups share the rules feature's own query
 * key, so a reader with a rule open has already paid for it.
 *
 * **A popover, not a `Modal`, and the choice is not cosmetic.** `Modal` is a `<dialog>` opened with
 * `showModal()`, which makes the rest of the page inert — correct for a question whose answer must be
 * given before anything else happens, which is why `ConfirmDialog` and the add-user form use it. This
 * is the opposite: a notification list is read *against* the page the reader is on, every row is a
 * link away from it, and blocking the page to show somebody a list they are about to leave would
 * make them close a dialog before they could act on it. So Escape and an outside click are wired by
 * hand here — the two behaviours `showModal` would have given free — and focus returns to the bell.
 * Deliberately **not** a focus trap, for `Drawer`'s stated reason: a hand-rolled trap has to
 * enumerate every focusable descendant and gets it wrong, and this panel never asks a blocking
 * question.
 *
 * **The one cost worth naming.** `useDatasetNames` walks the catalogue, so mounting this in the shell
 * means the walk happens on any page rather than only on the dashboard. It is one shared cache entry
 * with three other screens (the landing page is one of them, so it is warm in practice) and it is
 * bounded and reports its own truncation. The alternative was rendering a bare UUID in the row a
 * steward reads first, which is the defect this codebase has recorded five times.
 */

/** How the panel's three source reports are ordered, and it is the order they are argued in.
 *
 * Approvals first because they are the reason this component exists, and because a *blocked
 * governance transition* is the one row where the reader is the blocker. */
const SOURCE_ORDER = ['approvals', 'remediation', 'incidents', 'review'] as const;

export function NotificationBell(): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();

  /* **Three permission gates, each answered before the request is made — and it is a four-way answer
   * rather than a boolean.** A source the caller may not read contributes a *known* nothing and says
   * so; issuing the read anyway would turn an ordinary boundary into a 403 the panel has to report as
   * a fault — and a role that withholds one of these three reads is not hypothetical: `STEWARD`'s
   * reads were hand-listed where `READER`'s are derived, so the working role could not read
   * `observability.incident` at all for most of this platform's life.
   *
   * The fourth state is the one that matters most and was a defect in the first version of this
   * component. `can` denies while the identity read is in flight, so a boolean gate cannot tell "you
   * may not" from "we have not asked yet" — and the first reads as a known nothing, which made the
   * bell claim *nothing is waiting for you* for the length of every identity read. See `Access`. */
  const access = (permitted: boolean): Access =>
    identity.isPending
      ? 'unknown'
      : identity.isError
        ? 'unreadable'
        : permitted
          ? 'permitted'
          : 'forbidden';

  const rulesAccess = access(can(identity.data, READ_RULES_PERMISSION));
  const remediationAccess = access(can(identity.data, READ_PLANS_PERMISSION));
  const incidentsAccess = access(can(identity.data, READ_INCIDENTS_PERMISSION));
  const reviewAccess = access(can(identity.data, READ_CLASSIFICATIONS_PERMISSION));
  const maySign = can(identity.data, ACTIVATE_RULE_PERMISSION);

  const approvals = useApprovalFeed(rulesAccess);
  const incidents = useIncidentQueue({ enabled: incidentsAccess === 'permitted' });
  const review = useReviewQueue({ enabled: reviewAccess === 'permitted' });
  /* The queue's own key, so a reader with `/remediation` open has already paid for this and the
     bell costs them nothing — the rules half's reasoning, and one read rather than a fan-out
     because the server answers "may this caller sign" per row. */
  const plans = usePlanQueue({ enabled: remediationAccess === 'permitted' });
  const names = useDatasetNames();

  const [open, setOpen] = useState(false);
  /* The moment this component mounted, as the floor under `now` below. */
  const [mountedAt] = useState(() => Date.now());

  /* Pinned to the most recent successful read rather than taken fresh per render, so an age cannot
   * tick over mid-interaction and an unrelated state change cannot re-render every row. `HomeScreen`
   * sets the precedent: reading `dataUpdatedAt` as the *value* makes the dependency real, where a
   * `useMemo` that ignored it would measure from whenever the memo happened to run.
   *
   * **`mountedAt` is the floor, and that is a defect fixed rather than a nicety.** `dataUpdatedAt` is
   * `0` for a query that has never resolved — which includes every query this component *disables* on
   * a permission answer. So keying on the incident queue alone put `now` at the Unix epoch for anybody
   * without `read:observability.incident`, and every approval row then read "20,000 days ago". The
   * epoch-as-a-timestamp failure, arriving through a query that was deliberately never issued.
   *
   * `Math.max` over both readable sources plus the floor, so the claim stays exactly true: these ages
   * are as of the last successful read, and never earlier than this component appeared. */
  const now = useMemo(
    () => new Date(Math.max(mountedAt, incidents.dataUpdatedAt, review.dataUpdatedAt)),
    [mountedAt, incidents.dataUpdatedAt, review.dataUpdatedAt],
  );

  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(readDismissed);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    /* Only for a keyboard dismissal. Pulling focus back after an outside *click* would move it away
     * from whatever the reader just clicked, which is the opposite of what they asked for. */
    if (restoreFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close(true);
    };
    const onPointer = (event: MouseEvent): void => {
      const root = rootRef.current;
      /* `contains` on the whole widget, so a click on the bell itself is not an "outside" click that
       * closes the panel a moment before the button's own handler reopens it. */
      if (root !== null && event.target instanceof Node && !root.contains(event.target)) {
        close(false);
      }
    };
    document.addEventListener('keydown', onKey);
    /* `mousedown` rather than `click`: a row's `Link` unmounts the panel on navigation, and a `click`
     * listener would then see a target that is no longer in the document and read as outside. */
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open, close]);

  /* ------------------------------------------------------------ the feed */

  const items: Notification[] = [];

  for (const lookup of approvals.lookups) {
    const row = approvalNotification(
      {
        ruleId: lookup.rule.id,
        ruleName: lookup.rule.name,
        ruleHref: `${SCREEN_PATHS.rules}/${lookup.rule.id}`,
        request: lookup.request,
      },
      {
        maySign,
        isRequester: lookup.request.requester === identity.data?.subject,
        /* Read off the decisions already on the request rather than with another call — the payload
           the count comes from carries an `approver` per decision. See `Standing.hasDecided`. */
        hasDecided: lookup.request.decisions.some(
          (decision) => decision.approver === identity.data?.subject,
        ),
      },
    );
    if (row !== null) items.push(row);
  }

  if (incidents.isSuccess) {
    for (const incident of incidents.data) {
      /* `info` is a record rather than a request — the dashboard's `needsAttention` excludes it for
       * the same reason, and folding it in would make the badge go up when nothing actionable
       * happened, which is how a count stops being read. */
      if (incident.severity === 'info') continue;
      if (!incident.active) continue;
      items.push(
        incidentNotification({
          id: incident.id,
          severity: incident.severity,
          openedAt: incident.opened_at,
          /* The kind decides what the id refers to: a dataset for the four monitor kinds, a contract
           * for `contract_breach`. Naming a contract through the dataset map would report "not in the
           * catalogue" about something that was never a table. */
          subject: subjectIsDataset(incident.kind)
            ? nameDataset(incident.subject_id, names.isSuccess ? names.data : null)
            : { kind: 'gap', reasonKey: 'notifications.incident.subjectNotADataset' },
          href: SCREEN_PATHS.incidents,
        }),
      );
    }
  }

  if (plans.isSuccess) {
    for (const plan of plans.data) {
      /* **The server's answer, not a comparison made here.** `may_sign` is returned only when
         `kernel.approval` would accept this caller's signature — it runs the same checks the write
         path enforces, including the delegation chain, which is not published anywhere a client
         could see. Filtering on the state alone would put every pending plan in every reader's
         bell, including the requester's own and one they have already signed. */
      if (plan.decision_standing !== 'may_sign') continue;
      const relation = relationOf(plan);
      items.push(
        remediationApprovalNotification({
          planId: plan.id,
          /* Named from the plan's own proposals rather than the catalogue map every other row uses.
             A plan is a statement about one schema state, so the name it carries is the one that was
             true when it was made — and it needs no second fetch. */
          subject:
            relation === null
              ? { kind: 'gap', reasonKey: 'notifications.remediation.unnamedRelation' }
              : { kind: 'named', name: relation },
          submittedAt: plan.created_at,
          href: `${SCREEN_PATHS.remediation}/${plan.id}`,
        }),
      );
    }
  }

  if (review.isSuccess) {
    const row = reviewNotification(review.data.length, SCREEN_PATHS.review);
    if (row !== null) items.push(row);
  }

  const reports: readonly SourceReport[] = SOURCE_ORDER.map((source) => {
    if (source === 'approvals') {
      return {
        source,
        state: approvals.state,
        notLookedAt: approvals.notLookedAt,
        failed: approvals.failed,
      };
    }
    /* One table rather than a chain of ternaries, so a fifth source is an entry rather than another
       arm on two expressions that have to stay in step. */
    const readable = {
      remediation: { access: remediationAccess, query: plans },
      incidents: { access: incidentsAccess, query: incidents },
      review: { access: reviewAccess, query: review },
    }[source];
    return {
      source,
      /* `sourceStateFor` and not a chain of conditionals here, because the *order* is the content —
       * a query that was never issued is `isPending` for ever, so consulting the query first would
       * report a permission boundary as "still reading". Deciding that in one tested place is what
       * stops the call sites disagreeing. */
      state: sourceStateFor(readable.access, readable.query),
      notLookedAt: 0,
      failed: 0,
    };
  });

  const rows = orderNotifications(items);
  const badge = badgeFor(unreadCount(rows, dismissed), reports);
  const gaps = blindSpots(reports);

  /* Forget dismissals for rows that are gone, and **only once every source has answered.** Pruning
   * mid-load would drop the dismissal for a row that is about to come back, and it would come back
   * undismissed — a notification that reappears after somebody cleared it is worse than one that
   * lingers. `settled` is the guard, not an optimisation. */
  const settled = reports.every(
    (report) => report.state === 'ok' || report.state === 'forbidden',
  );
  /* Keyed on the ids as a *string*, not on `rows`. `rows` is a fresh array every render, so a memo
   * over it never hits — the Set would get a new identity each time and the effect below would run
   * `setDismissed` on every render. It bails out when nothing changed, so there is no loop; it is
   * still a state call per render on a component mounted for the whole session, and a `useMemo` that
   * cannot memoise reads as though it does.
   *
   * `|` is safe as the separator because every id this model mints is built from a kind, a UUID, a
   * phase name and a digit — never from tenant-supplied text. The empty string is handled explicitly
   * rather than left to `split`, which answers `['']` for it and would put a phantom id in the set. */
  const liveKey = rows.map((row) => row.id).join('|');
  const liveIds = useMemo(
    () => new Set(liveKey === '' ? [] : liveKey.split('|')),
    [liveKey],
  );
  useEffect(() => {
    if (!settled) return;
    setDismissed((current) => {
      const kept = pruneDismissed(current, liveIds);
      /* Only write when something actually left, so this effect cannot loop: `setDismissed` with a
       * new Set of the same members would change the reference and re-run the memo below it. */
      if (kept.length === current.size) return current;
      writeDismissed(kept);
      return new Set(kept);
    });
  }, [settled, liveIds]);

  const dismiss = useCallback((id: string) => {
    setDismissed((current) => {
      const next = [...current, id];
      writeDismissed(next);
      return new Set(next);
    });
  }, []);

  /* The accessible name carries the whole state, because the badge is a glyph and a digit. A reader
   * on a screen reader has to get "four things need you, and some sources could not be read" from
   * the button, since neither the `+` nor its colour is available to them. */
  const buttonLabel =
    badge.kind === 'reading'
      ? t('notifications.label.reading')
      : badge.kind === 'none'
        ? t('notifications.label.none')
        : badge.partial
          ? t('notifications.label.partial', { count: badge.count })
          : t('notifications.label.count', { count: badge.count });

  const visible = rows.filter((row) => !dismissed.has(row.id));

  return (
    <div className="lq-notif" ref={rootRef}>
      <button
        type="button"
        ref={buttonRef}
        className="lq-icon-button lq-notif__button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={buttonLabel}
        title={buttonLabel}
        onClick={() => {
          setOpen(!open);
        }}
      >
        {/* An SVG rather than a glyph, for the reason the search icon and the nav icons are SVG: a
            bell character renders as a different shape on every face, and at 15px `U+1F514` is an
            emoji on some platforms and a monochrome outline on others. */}
        <svg
          className="lq-notif__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7" />
          <path d="M10.3 20a2 2 0 0 0 3.4 0" />
        </svg>

        {/* **The badge is absent only when the answer is a complete zero.** `badgeFor` is what makes
            that true: a source still loading, one that failed, or a fan-out that stopped short all
            render a mark, because a bell with nothing on it is a claim that there is nothing to do
            and it is the strongest claim this component makes. */}
        {badge.kind === 'none' ? null : (
          <span className="lq-notif__badge" data-kind={badge.kind} aria-hidden="true">
            {badge.kind === 'reading' ? (
              '·'
            ) : (
              <>
                <span className="lumiq-numeric">{badge.count}</span>
                {/* A `+`, not an asterisk or a colour change. "At least four" is exactly the fact,
                    and it reads that way without a legend. */}
                {badge.partial ? '+' : null}
              </>
            )}
          </span>
        )}
      </button>

      {open ? (
        /* A labelled region rather than `role="dialog"`. A dialog is a thing that has to be answered;
           this is a list, and announcing it as a dialog without `aria-modal` tells a screen reader
           the page is blocked when it is not. */
        <section
          id={panelId}
          className="lq-notif__panel"
          aria-label={t('notifications.panel.label')}
        >
          <header className="lq-notif__head">
            <h2 className="lq-notif__title">{t('notifications.panel.title')}</h2>
            <button
              type="button"
              className="lq-icon-button"
              aria-label={t('common.close')}
              onClick={() => {
                close(true);
              }}
            >
              <span aria-hidden="true">✕</span>
            </button>
          </header>

          {/* Above the list, because these make it *shorter* while looking complete — a reader must
              not conclude "less needs me" from a request that failed or a walk that stopped. The
              dashboard's `unreadableSources` band is the same decision. */}
          {gaps.length > 0 ? (
            <ul className="lq-notif__gaps">
              {gaps.map((gap) => (
                <li key={gap.source}>
                  {gap.state === 'ok'
                    ? null
                    : t(SOURCE_STATE_KEY[gap.state], {
                        source: t(SOURCE_LABEL_KEY[gap.source]),
                      })}
                  {gap.notLookedAt > 0
                    ? ` ${t('notifications.blind.notLookedAt', { count: gap.notLookedAt })}`
                    : null}
                  {gap.failed > 0
                    ? ` ${t('notifications.blind.failed', { count: gap.failed })}`
                    : null}
                </li>
              ))}
            </ul>
          ) : null}

          {visible.length === 0 ? (
            <p className="lq-notif__empty">
              {rows.length === 0
                ? t('notifications.empty')
                : /* Everything here was dismissed, which is a different fact from nothing being
                     here — and the one a reader who cleared the list on another device needs. */
                  t('notifications.allDismissed', { count: rows.length })}
            </p>
          ) : (
            <ul className="lq-notif__list">
              {visible.map((row) => {
                const age =
                  row.at === null ? null : describeAge(minutesSince(row.at, now));
                return (
                  <li className="lq-notif__row" data-tone={row.tone} key={row.id}>
                    <span className="lq-notif__rule" aria-hidden="true" />
                    <div className="lq-notif__body">
                      <p className="lq-notif__what">
                        {t(row.messageKey, row.messageArgs)}
                      </p>
                      <p className="lq-notif__subject">
                        {/* Mono only for a real identifier. A sentence explaining why the name is
                            missing, set in monospace, reads as though the sentence *were* the
                            name. */}
                        {row.subject.kind === 'named' ? (
                          <span className="lq-mono">{row.subject.name}</span>
                        ) : (
                          <span className="lq-muted">{t(row.subject.reasonKey)}</span>
                        )}
                      </p>
                      <p className="lq-notif__meta">
                        {/* No time at all rather than a dash for a row that has none: the review
                            queue is a depth, and "—" beside it invites the reading that we failed to
                            record when it arrived. */}
                        {age === null
                          ? null
                          : t(`notifications.age.${age.unit}`, { count: age.count })}
                        {/* **Says which of the two it is.** A row the reader cannot act on is still
                            worth showing — an author watching their own request is the case — and
                            offering them a signature the server refuses by identity would leave them
                            guessing whether they lacked a permission or were the requester. */}
                        {age === null ? null : ' · '}
                        <span className="lq-muted">
                          {row.actionable
                            ? t('notifications.actionable')
                            : t('notifications.watching')}
                        </span>
                      </p>
                    </div>
                    <div className="lq-notif__controls">
                      <Link
                        to={row.to}
                        onClick={() => {
                          close(false);
                        }}
                      >
                        {t('notifications.open')}
                      </Link>
                      <button
                        type="button"
                        className="lq-link-button"
                        onClick={() => {
                          dismiss(row.id);
                        }}
                      >
                        {t('notifications.dismiss')}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {/* **Stated, not hidden.** There is no server-side read state and this does not invent one,
              so a dismissal is a preference belonging to this browser — exactly like the theme and the
              sidebar's collapse. A reader who clears the list on a laptop and finds it full on a
              phone should have been told, rather than concluding the feature is broken. */}
          <p className="lq-notif__foot">{t('notifications.perBrowser')}</p>
        </section>
      ) : null}
    </div>
  );
}
